<template>
  <div @keydown="onKeydown">
    <n-modal-body class="add-recipient-modal" width="m">
      <NTitleBar
        :title="$t('shareSpace.addRecipient')"
        :subtitle="$t('shareSpace.addRecipientSubtitle')"
        closable
        @close="$emit('close')"
        class="title"
        :backButton="!noBackButton"
        :backButtonLabel="$t('general.back')"
        @back="$emit('back')"
      />
      <ValidationObserver ref="form">
        <div class="add-recipient-form">
          <div class="name-form">
            <ValidationProvider rules="required" name="Firstname" v-slot="{ errors }">
              <div class="form-element">
                <n-input
                  class="input"
                  variableWidth
                  autofocus
                  v-model="recipient.firstName"
                  :label="$t('shareSpace.firstName')"
                  :placeholder="$t('shareSpace.john')"
                  :error="errors[0]"
                />
              </div>
            </ValidationProvider>
            <ValidationProvider rules="required" name="Lastname" v-slot="{ errors }">
              <div class="form-element">
                <n-input
                  class="input"
                  variableWidth
                  v-model="recipient.lastName"
                  :label="$t('shareSpace.lastName')"
                  :placeholder="$t('shareSpace.doe')"
                  :error="errors[0]"
                ></n-input>
              </div>
            </ValidationProvider>
          </div>
          <ValidationProvider rules="required|emailplusimpersonation" name="EmailAddress" v-slot="{ errors }">
            <div class="form-element">
              <n-input
                class="input"
                variableWidth
                v-model="recipient.email"
                :label="$t('shareSpace.emailAddress')"
                :placeholder="$t('shareSpace.johnDoeEmail')"
                :error="errors[0]"
              ></n-input>
            </div>
          </ValidationProvider>
        </div>
      </ValidationObserver>
      <n-modal-footer>
        <template #right>
          <n-button variant="secondary" :label="$t('general.cancel')" @click="$emit('close')" class="margin-right" />
          <n-button @click="addRecipient" icon="check" :label="$t('shareSpace.addRecipient')" :loading="loading" />
        </template>
      </n-modal-footer>
    </n-modal-body>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import NTitleBar from '@/modules/global/components/core/NTitleBar.vue'
import { ValidationObserver } from 'vee-validate'
import Space from '../../models/Space'
import { mapState, mapStores } from 'pinia'
import { SpaceStore, useSpaceStore } from '../../stores/SpaceStore'
import { useSnackbarStore, SnackbarStore } from '@/modules/global/stores/SnackbarStore'
import { UserStore, useUserStore } from '@/modules/user/stores/UserStore'
import AddRecipientRequest from '@/modules/space/models/AddRecipientRequest'
import User from '@/modules/user/models/User'
import { isTrialSoftLockedError } from '@/modules/featurerestriction/trialSoftLock'

@Component({
  computed: {
    ...mapState(useSpaceStore, ['currentSpace']),
    ...mapState(useUserStore, ['users']),
    ...mapStores(useSpaceStore, useSnackbarStore)
  },
  components: {
    NTitleBar
  }
})
export default class AddRecipient extends Vue {
  @Prop({ default: false }) noBackButton!: boolean
  @Prop({ default: '' }) initialEmail!: string

  currentSpace!: Space
  users!: User[]

  recipient: AddRecipientRequest = new AddRecipientRequest()

  loading = false
  spaceStore!: SpaceStore
  userStore!: UserStore
  snackbarStore!: SnackbarStore

  @Ref('form') form!: InstanceType<typeof ValidationObserver>

  mounted() {
    this.recipient.email = this.initialEmail
  }

  async addRecipient() {
    const isValid = await this.form.validate()
    if (!isValid) {
      return
    }
    this.loading = true
    try {
      const createdUser = await this.spaceStore.addRecipient(this.currentSpace.id, this.recipient)
      this.snackbarStore.enqueue({
        label: this.$t('shareSpace.addRecipientSuccess') as string,
        leadingIcon: true,
        variant: 'success'
      })
      const user =
        createdUser ?? this.users.find(user => user.email.toLowerCase() === this.recipient.email.toLowerCase())
      if (user) {
        this.$emit('select', user.id)
      }
      this.$emit('back')
    } catch (error: any) {
      if (isTrialSoftLockedError(error)) {
        return
      }
      const errorCode = error?.response?.data?.code as string | undefined
      const errorKey = errorCode ? `inviteMembers.addRecipientError.${errorCode}` : undefined
      const label =
        errorKey && this.$i18n.te(errorKey)
          ? (this.$t(errorKey) as string)
          : (this.$t('inviteMembers.inviteError') as string)
      this.snackbarStore.enqueue({
        label,
        leadingIcon: true,
        variant: 'alert'
      })
    } finally {
      this.loading = false
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.addRecipient()
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.add-recipient-modal {
  padding: $notch-space-8x;

  .title {
    margin-bottom: $notch-space-6x;
  }

  .add-recipient-form {
    display: flex;
    flex-direction: column;
    gap: $notch-space-4x;

    .name-form {
      display: flex;
      gap: $notch-space-2x;

      span {
        flex: 1;
      }
    }

    .input {
      ::v-deep label {
        padding: 0;
      }

      ::v-deep fieldset {
        margin-left: 0;
      }
    }
  }
}
</style>
